import { useGetAllOwnAppraisalQuery } from "@/app/features/employee/performance/performance.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon, LaptopPerformanceIcon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { countParPageSerialNumber } from "@/utils/global.utils";
import { Button, Flex, type TableProps } from "antd";
import LogViewer from "./LogViewer";
import ManageOwnAppraisal from "./ManageOwnAppraisal";
import ViewAppraisal from "./ViewAppraisal";

const APPRAISAL_STATUS_CONFIG = [
  {
    label: "Not Started",
    value: "NOT_STARTED",
    className: "status status-slate",
  },
  {
    label: "Draft",
    value: "DRAFT",
    className: "status status-fuchsia",
  },
  {
    label: "Pending",
    value: "PENDING",
    className: "status status-yellow",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Approved",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

const OwnAppraisal = () => {
  const { userId, organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.EMP_OWN_APPRAISAL,
  );
  const [config, handleOpen, handleClose] = useDiscloser<
    "MANAGE" | "VIEW" | "LOG"
  >();

  const { data, isFetching } = useGetAllOwnAppraisalQuery({
    organization_id: organizationId,
    employee_id: userId,
    page,
    limit,
    ...filters,
  });

  const appraisals = data?.data?.appraisals || [];
  const total = data?.data?.pagination?.total || 0;

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL. No.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: ["plan", "planName"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Plan Period",
      dataIndex: ["plan", "planPeriod"],
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Plan Type",
      dataIndex: ["plan", "planType"],
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Due Date",
      dataIndex: "appraisalDueDate",
      align: "center",
      render: (val: string) => formatDate(val),
    },
    {
      title: "Status",
      dataIndex: "appraisalStatus",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          shouldCapitalize={false}
          config={APPRAISAL_STATUS_CONFIG}
        />
      ),
    },
    {
      width: 150,
      title: "Actions",
      render: (record: any) => (
        <Flex gap={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            onClick={() => {
              if (
                record.appraisalStatus === "DRAFT" ||
                record.appraisalStatus === "PENDING"
              ) {
                handleOpen({
                  type: "MANAGE",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                    planId: record.planId,
                  },
                });
              } else {
                handleOpen({
                  type: "VIEW",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                  },
                });
              }
            }}
          />
          <Button
            size="small"
            className="btn-primary-bordered h-8 flex items-center gap-2"
            onClick={() => {
              handleOpen({
                type: "LOG",
                data: {
                  planName: record.plan?.planName,
                  mappingId: record.id,
                  dateTime: record.updatedAt
                    ? formatDate(record.updatedAt)
                    : "",
                },
              });
            }}
          >
            <LaptopPerformanceIcon className="text-theme-primary size-4" /> Log
            View
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={appraisals}
        onPaginate={handlePagination}
        customTitle="Own Appraisal List"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={total}
        filterSection={<></>}
      />

      <ViewAppraisal
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
      />

      <ManageOwnAppraisal
        open={config.open && config.type === "MANAGE"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        planId={config.data?.planId}
      />

      <LogViewer
        open={config.open && config.type === "LOG"}
        onClose={handleClose}
        id={config.data?.mappingId}
        planName={config.data?.planName}
        dateTime={config.data?.dateTime}
      />
    </>
  );
};

export default OwnAppraisal;
